import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { shortLabel } from '@gogrov/shared'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useTopics, usePlan } from '@/hooks/useConfig'
import { usePills, useClientPrefs } from '@/hooks/useMix'
import { ROUTES } from '@/components/gg/chrome/nav'
import { useWatchState, useBreakingSummary } from '@/hooks/useAlerts'
import { useRecordsSummary } from '@/hooks/useRecords'
import type { Plan } from '@/types/database'
import type { ControlPill, CoveredPlace, FeedNudge, Place } from '@/types/controls'

/**
 * Feed controls (Brian's after/feed-controls.js, settings-topics.html v3.1,
 * settings-places.html v1.9): the hooks behind the sheet, Topics and Places.
 */

// ---------------- Pull now ----------------

/**
 * Pull now on Topics (Brian, v1.5: "Pull now rebuilds today's brief from the new
 * mix at once instead of waiting for tomorrow's") lands on Your feed with
 * `?refresh=mix`. The feed reads it, deals a fresh batch from the saved mix
 * (free, as in the prototype), and drops the param.
 */
export const FEED_REFRESH_PARAM = 'refresh'
export const FEED_REFRESH_MIX = 'mix'
export const feedRefreshUrl = () => `${ROUTES.feed}?${FEED_REFRESH_PARAM}=${FEED_REFRESH_MIX}`

/** Missing tables (another area's migration not applied yet) read as empty. */
const missing = (e: { code?: string; message?: string } | null) =>
  !!e && (e.code === '42P01' || e.code === 'PGRST205' || /does not exist|schema cache/i.test(e.message ?? ''))

// ---------------- Pills ----------------

/** Every pill with the rest clock and season window (my_pills, 20240036). */
export function useControlPills() {
  const q = usePills()
  return { ...q, data: q.data as ControlPill[] | undefined }
}

/** Rest a pill until a day, or wake it (until null). Waking needs room. */
export function useSetPillRest() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id: string; until: string | null; forceAwake?: boolean }) => {
      const { error } = await supabase.rpc('set_pill_rest', {
        p_pill_id: args.id, p_until: args.until, p_force_awake: args.forceAwake ?? false,
      })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pills', clientId] }),
  })
}

/** Pills whose topic left the mix go with it (Brian: a pill lives in its topic). */
export async function dropOrphanPills(clientId: string, topics: string[]) {
  if (!topics.length) return
  await supabase.from('keyword_pills').delete().eq('client_id', clientId)
    .not('topic', 'in', `(${topics.map((t) => `"${t}"`).join(',')})`)
}

// ---------------- Plans ----------------

export function useAllPlans() {
  return useQuery({
    queryKey: ['plans-all'],
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('plans').select('*').eq('active', true).order('sort_order')
      if (error) throw error
      return data as Plan[]
    },
  })
}

/** The plan above this one, or null on the top plan. */
export function useNextPlan() {
  const { data: plan } = usePlan()
  const { data: plans } = useAllPlans()
  if (!plan || !plans) return null
  const i = plans.findIndex((p) => p.id === plan.id)
  return i >= 0 && i < plans.length - 1 ? plans[i + 1] : null
}

// ---------------- Places ----------------

/** The account's places, home first, then the order they were added. */
export function usePlaces() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['regions', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('regions').select('*').eq('client_id', clientId!)
        .order('created_at', { ascending: true })
      if (error) throw error
      return data as Place[]
    },
    select: (rows) => [...rows].sort((a, b) => Number(!!b.home) - Number(!!a.home)),
  })
}

/** The places GoGrov covers (the Add a place search). */
export function useCoveredPlaces() {
  return useQuery({
    queryKey: ['covered-places'],
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('covered_places').select('*').order('name')
      if (missing(error)) return [] as CoveredPlace[]
      if (error) throw error
      return data as CoveredPlace[]
    },
  })
}

const idem = (kind: string, ...parts: string[]) => `${kind}:${parts.join(':')}:${Math.floor(Date.now() / 1000)}`

/** At the cap: the new place in, the one it replaces out, 5 seeds, at once. */
export function useSwapPlace() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { outId: string; name: string; scope: Place['scope'] }) => {
      const { data, error } = await supabase.rpc('swap_place', {
        p_out: args.outId, p_name: args.name, p_scope: args.scope, p_idem_key: idem('swap', args.outId, args.name),
      })
      if (error) throw error
      return data as string
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['regions', clientId] })
      qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

/** A new home place; the old one goes. 5 seeds. */
export function useChangeHome() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { name: string; scope: Place['scope'] }) => {
      const { data, error } = await supabase.rpc('change_home_place', {
        p_name: args.name, p_scope: args.scope, p_idem_key: idem('home', clientId ?? '', args.name),
      })
      if (error) throw error
      return data as string
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['regions', clientId] })
      qc.invalidateQueries({ queryKey: ['seed-balance', clientId] })
    },
  })
}

// ---------------- Fewer like this ----------------

/** The nudges from × › Fewer like this on the feed (feed_nudges, 20240034). */
export function useFeedNudges() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['feed-nudges', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase.from('feed_nudges').select('*').eq('client_id', clientId!)
        .order('updated_at', { ascending: false })
      if (missing(error)) return [] as FeedNudge[]
      if (error) throw error
      return data as FeedNudge[]
    },
  })
}

export function useUndoNudge() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (n: Pick<FeedNudge, 'kind' | 'key'>) => {
      const { error } = await supabase.from('feed_nudges').delete()
        .eq('client_id', clientId!).eq('kind', n.kind).eq('key', n.key)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feed-nudges', clientId] }),
  })
}

// ---------------- Breaking news and public records (other areas) ----------------

// ---------------- The one-line summaries ----------------

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/**
 * What each feed control is set to now (Brian's rows() in feed-controls.js),
 * and the "Tuned for …" line for under the feed's title (tuned(): the home place
 * on the first line, the rest below).
 */
export function useFeedControlsSummary() {
  const { data: topics } = useTopics()
  const { data: pills } = useControlPills()
  const { data: places } = usePlaces()
  const { data: plan } = usePlan()
  // The Breaking news and Public records areas' own summaries (watch_state,
  // records_state; migration 20240037).
  const { data: watch } = useWatchState()
  const brkLine = useBreakingSummary()
  const recLine = useRecordsSummary()
  const { data: prefs } = useClientPrefs()

  const mix = [...(topics ?? [])].filter((t) => (t.share ?? 0) > 0).sort((a, b) => b.share - a.share)
  // Brian's GG.keywords(): every pill that can be a keyword hit (places and
  // resting pills included), not the too-broad ones or anything under 3 letters.
  const kw = (pills ?? []).filter((p) => p.term.trim().length > 2 && p.pill_type !== 'broad').length
  const list = places ?? []

  const topicsLine = mix.length
    ? mix.slice(0, 2).map((m) => `${shortLabel(m.topic)} ${m.share}%`).join(', ')
      + (mix.length > 2 ? ` + ${mix.length - 2} more` : '') + (kw ? ` · ${plural(kw, 'keyword')}` : '')
    : 'None yet · pick a few'
  const placesLine = list.length
    ? list.slice(0, 3).map((p, i) => `${p.name}${i === 0 && p.home ? ' (home)' : ''}`).join(', ')
      + (list.length > 3 ? ` + ${list.length - 3} more` : '')
    : 'None yet · add your town'
  const breakingLine = brkLine ?? 'None yet · add one'
  const recordsLine = plan && !(plan.records_budget ?? 0) ? 'Not on your plan'
    : recLine ?? (plan ? 'Off · turn on permits, licenses, agendas' : '')

  const home = list[0]?.name ?? prefs?.city_state?.split(',')[0] ?? 'your town'
  const more = Math.max(0, list.length - 1)
  const tuned = {
    l1: `Tuned for ${home}`,
    l2: [more > 0 ? `+ ${plural(more, 'place')}` : '', plural(mix.length, 'topic'),
      watch?.paused ? 'watches paused' : plural(watch?.watches.length ?? 0, 'watch', 'watches')].filter(Boolean).join(' · '),
  }
  return { topicsLine, placesLine, breakingLine, recordsLine, tuned, ready: !!topics && !!places }
}
